import { useEffect, useState } from 'react';
import { ArrowLeft, Ban, Check, Copy, Globe, RefreshCw, Send, UserPlus, Users, X } from 'lucide-react';
import { formatFriendCode, type FriendView, type Presence } from '@gmp/shared-types/browser';
import { Avatar, Stage } from '@gmp/shared-ui';
import { navigate } from '../app/router';
import { sound } from '../app/prefs';
import { useAccountsConfig } from '../app/serverConfig';
import { usePartyState } from '../App';
import { party } from '../net/client';
import { SignInSheet } from './Account';
import { SERVER_URL } from '../net/config';
import { ART } from '../app/art';
import type { AvatarId } from '@gmp/shared-types/browser';

/** Friends only: presence is shown to accepted friends; nobody else ever sees whether you are online. */
function presenceLabel(p: Presence) {
  if (p.status === 'in_party') return `${p.stage === 'lobby' ? 'In a lobby' : 'Playing'}${p.game ? `: ${p.game}` : ''}`;
  return p.status === 'online' ? 'Online' : 'Offline';
}

export function PresenceDot({ p }: { p: Presence }) {
  const color = p.status === 'offline' ? 'bg-mute/50' : p.status === 'in_party' ? 'bg-[var(--accent-2)]' : 'bg-good';
  return <span className={`inline-block size-2.5 rounded-full shrink-0 ${color}`} aria-hidden />;
}

const say = (r: { ok: boolean; error?: string }) => party.notify(r.error ?? (r.ok ? 'Done' : 'Something went wrong'), r.ok ? 'good' : 'bad');

/** Invite button: only for online friends, only while you are in a party. */
function InviteButton({ f, inParty }: { f: FriendView; inParty: boolean }) {
  const [sent, setSent] = useState(false);
  if (f.presence.status === 'offline' || !inParty) return null;
  return (
    <button
      className="btn btn-sm"
      disabled={sent}
      onClick={() => {
        sound.play('tap');
        setSent(true);
        void party.social({ op: 'invite', accountId: f.accountId }).then((r) => {
          say(r);
          if (!r.ok) setSent(false);
          else setTimeout(() => setSent(false), 15_000);
        });
      }}
      data-testid={`invite-${f.displayName}`}
    >
      <Send size={16} aria-hidden /> {sent ? 'Invited' : 'Invite'}
    </button>
  );
}

function FriendRow({ f, inParty, manage }: { f: FriendView; inParty: boolean; manage: boolean }) {
  const [menu, setMenu] = useState(false);
  return (
    <li className="card p-3 grid gap-2" data-testid={`friend-${f.displayName}`}>
      <div className="flex items-center gap-3">
        <Avatar id={f.avatar} size={40} />
        <div className="min-w-0 flex-1">
          <p className="font-bold truncate">{f.displayName}</p>
          <p className="text-xs text-mute flex items-center gap-1.5" data-testid={`presence-${f.displayName}`}><PresenceDot p={f.presence} /> {presenceLabel(f.presence)}</p>
        </div>
        <InviteButton f={f} inParty={inParty} />
        {manage && <button className="btn btn-sm btn-ghost" aria-expanded={menu} aria-label={`More for ${f.displayName}`} onClick={() => setMenu(!menu)}>…</button>}
      </div>
      {menu && (
        <div className="flex gap-2 flex-wrap">
          <button className="btn btn-sm btn-secondary" onClick={() => void party.social({ op: 'remove', accountId: f.accountId }).then(say)}><X size={16} aria-hidden /> Remove friend</button>
          <button className="btn btn-sm btn-secondary text-bad" onClick={() => void party.social({ op: 'block', accountId: f.accountId }).then(say)}><Ban size={16} aria-hidden /> Block</button>
        </div>
      )}
    </li>
  );
}

/** Lobby → Invite sheet: online friends with an Invite button (pre-approved when the host invites). */
export function InviteFriends() {
  const { signedIn, social } = usePartyState();
  const accounts = useAccountsConfig();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (signedIn) void party.social({ op: 'get' });
  }, [signedIn]);
  if (!signedIn) {
    if (!accounts) return null;
    return (
      <div className="card p-4 w-full grid gap-2 text-left">
        <p className="font-display font-black text-lg flex items-center gap-2"><Users size={20} aria-hidden /> Invite friends</p>
        <p className="text-mute text-sm">Sign in to invite friends who are online straight into this party.</p>
        <button className="btn btn-sm btn-secondary justify-self-start" onClick={() => setOpen(true)} data-testid="invite-friends-signin">Sign in</button>
        <SignInSheet open={open} onClose={() => setOpen(false)} />
      </div>
    );
  }
  const online = social?.friends.filter((f) => f.presence.status !== 'offline') ?? [];
  return (
    <div className="card p-4 w-full grid gap-3 text-left" data-testid="invite-friends">
      <p className="font-display font-black text-lg flex items-center gap-2"><Users size={20} aria-hidden /> Invite friends</p>
      {!social ? <p className="text-mute text-sm">Loading…</p> : online.length ? (
        <ul className="grid gap-2">{online.map((f) => <FriendRow key={f.accountId} f={f} inParty manage={false} />)}</ul>
      ) : (
        <p className="text-mute text-sm">None of your friends are online right now.{social.friends.length ? '' : ' Share your friend code to add some.'}</p>
      )}
      <button className="btn btn-sm btn-ghost justify-self-start" onClick={() => navigate({ name: 'friends' })} data-testid="open-friends">All friends</button>
    </div>
  );
}

export function FriendsScreen() {
  const { signedIn, social, room, status } = usePartyState();
  const accounts = useAccountsConfig();
  const [code, setCode] = useState('');
  const [signIn, setSignIn] = useState(false);
  useEffect(() => {
    if (signedIn && status === 'online') void party.social({ op: 'get' });
  }, [signedIn, status]);
  const inParty = !!room && room.you.role !== 'display';
  const back = () => (inParty ? navigate({ name: 'party' }) : navigate({ name: 'home' }));
  return (
    <Stage>
      <main className="flex-1 flex flex-col gap-5 safe-pad safe-top safe-bottom py-6 max-w-lg w-full mx-auto">
        <header className="flex items-center gap-3">
          <button className="btn btn-sm btn-secondary" onClick={back} aria-label={inParty ? 'Back to the party' : 'Back'}><ArrowLeft size={18} /></button>
          <h1 className="font-display font-black text-3xl">Social</h1>
        </header>
        <OpenGames inParty={inParty} />
        <h2 className="font-display font-black text-2xl -mb-2 flex items-center gap-2"><Users size={22} aria-hidden /> Friends</h2>
        {!signedIn ? (
          <section className="card p-5 grid gap-3">
            {accounts ? (
              <>
                <p>Sign in to get a friend code, add friends and invite them to your parties. Only your friends can see when you are online.</p>
                <button className="btn justify-self-start" onClick={() => setSignIn(true)} data-testid="friends-signin">Sign in</button>
              </>
            ) : <p className="text-mute">Accounts aren’t available on this server yet. You can still share a party code or link.</p>}
            <SignInSheet open={signIn} onClose={() => setSignIn(false)} />
          </section>
        ) : !social ? <p className="text-mute">Loading…</p> : (
          <>
            {inParty && <p className="chip text-sm py-2 px-3 self-start">You’re in party {room.code}: online friends can be invited.</p>}
            <section className="card p-5 grid gap-3" aria-label="Add a friend">
              <div className="flex items-center gap-3">
                <div className="min-w-0">
                  <p className="text-xs text-mute">Your friend code</p>
                  <p className="selectable font-display font-black text-2xl tracking-wider tabular-nums" data-testid="friends-my-code">{formatFriendCode(social.me.friendCode)}</p>
                </div>
                <button className="btn btn-sm btn-secondary ml-auto" aria-label="Copy friend code" onClick={() => navigator.clipboard?.writeText(formatFriendCode(social.me.friendCode)).then(() => party.notify('Friend code copied', 'good'))}><Copy size={16} /></button>
              </div>
              <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); void party.social({ op: 'add_code', code }).then((r) => { say(r); if (r.ok) setCode(''); }); }}>
                <label className="sr-only" htmlFor="friend-code">Friend code</label>
                <input id="friend-code" className="field flex-1 uppercase tracking-wider" placeholder="ABCD-EFGH" autoCapitalize="characters" autoComplete="off" maxLength={12} value={code} onChange={(e) => setCode(e.target.value)} data-testid="friend-code-input" />
                <button className="btn" disabled={code.replace(/[^A-Za-z0-9]/g, '').length !== 8} data-testid="friend-code-add"><UserPlus size={18} aria-hidden /> Add</button>
              </form>
            </section>

            {social.invites.length > 0 && (
              <section className="grid gap-2" aria-label="Party invites">
                <h2 className="font-display font-black text-xl">Party invites</h2>
                {social.invites.map((i) => <p key={i.id} className="text-sm text-mute">{i.from.displayName} invited you to {i.gameName}. Use the card at the bottom to join.</p>)}
              </section>
            )}

            {social.incoming.length > 0 && (
              <section className="grid gap-2" aria-label="Friend requests">
                <h2 className="font-display font-black text-xl">Requests</h2>
                <ul className="grid gap-2">
                  {social.incoming.map((p) => (
                    <li key={p.accountId} className="card p-3 flex items-center gap-3" data-testid={`request-${p.displayName}`}>
                      <Avatar id={p.avatar} size={40} />
                      <p className="font-bold flex-1 truncate">{p.displayName}</p>
                      <button className="btn btn-sm" onClick={() => void party.social({ op: 'respond', accountId: p.accountId, accept: true }).then(say)} data-testid={`accept-${p.displayName}`}><Check size={16} aria-hidden /> Accept</button>
                      <button className="btn btn-sm btn-ghost" aria-label={`Decline ${p.displayName}`} onClick={() => void party.social({ op: 'respond', accountId: p.accountId, accept: false }).then(say)}><X size={16} /></button>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <section className="grid gap-2" aria-label="Friends list">
              <h2 className="font-display font-black text-xl">Friends ({social.friends.length})</h2>
              {social.friends.length ? (
                <ul className="grid gap-2">{social.friends.map((f) => <FriendRow key={f.accountId} f={f} inParty={inParty} manage />)}</ul>
              ) : <p className="text-mute text-sm">No friends yet. Share your code, or add people you’ve played with below.</p>}
              {!inParty && social.friends.some((f) => f.presence.status !== 'offline') && <p className="text-xs text-mute">Start or join a party to invite online friends.</p>}
            </section>

            {social.outgoing.length > 0 && (
              <section className="grid gap-2" aria-label="Sent requests">
                <h2 className="font-display font-black text-lg text-mute">Waiting for them</h2>
                <ul className="grid gap-2">
                  {social.outgoing.map((p) => (
                    <li key={p.accountId} className="flex items-center gap-3 text-sm">
                      <Avatar id={p.avatar} size={28} /> <span className="flex-1 truncate">{p.displayName}</span>
                      <button className="btn btn-sm btn-ghost" onClick={() => void party.social({ op: 'remove', accountId: p.accountId }).then(say)}>Cancel</button>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {social.recent.length > 0 && (
              <section className="grid gap-2" aria-label="Played with recently">
                <h2 className="font-display font-black text-xl">Played with recently</h2>
                <ul className="grid gap-2">
                  {social.recent.map((r) => (
                    <li key={r.accountId} className="card p-3 flex items-center gap-3" data-testid={`recent-${r.nickname}`}>
                      <Avatar id={r.avatar} size={36} />
                      <span className="flex-1 truncate font-bold">{r.nickname}</span>
                      {r.relation === 'none' ? (
                        <button className="btn btn-sm btn-secondary" onClick={() => void party.social({ op: 'add_recent', accountId: r.accountId }).then(say)}><UserPlus size={16} aria-hidden /> Add</button>
                      ) : <span className="text-xs text-mute">{r.relation === 'friend' ? 'Friend' : r.relation === 'outgoing' ? 'Request sent' : 'Wants to be friends'}</span>}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {social.blocked.length > 0 && (
              <details className="text-sm">
                <summary className="cursor-pointer text-mute">Blocked ({social.blocked.length})</summary>
                <ul className="grid gap-2 mt-2">
                  {social.blocked.map((p) => (
                    <li key={p.accountId} className="flex items-center gap-3">
                      <span className="flex-1 truncate">{p.displayName}</span>
                      <button className="btn btn-sm btn-ghost" onClick={() => void party.social({ op: 'unblock', accountId: p.accountId }).then(say)}>Unblock</button>
                    </li>
                  ))}
                </ul>
              </details>
            )}
            <p className="text-xs text-mute">Only accepted friends see whether you are online or which game you are playing (never your party code), and only they can invite you. Blocked players can’t see or invite you.</p>
          </>
        )}
      </main>
    </Stage>
  );
}

type OpenGame = { code: string; gameId: string; game: string; host: string; hostAvatar: AvatarId; players: number; bots: number; capacity: number; approval: boolean; createdAt: number };

/** Social → Open games: public parties waiting in their lobby (the host turned on "Public party"). Anyone can join. */
function OpenGames({ inParty }: { inParty: boolean }) {
  const [games, setGames] = useState<OpenGame[] | null>(null);
  const [error, setError] = useState(false);
  const [n, setN] = useState(0);
  useEffect(() => {
    let live = true;
    const load = () =>
      fetch(`${SERVER_URL}/api/parties/public`)
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
        .then((d: { parties: OpenGame[] }) => live && (setGames(d.parties), setError(false)))
        .catch(() => live && setError(true));
    void load();
    const id = window.setInterval(load, 10_000);
    return () => {
      live = false;
      clearInterval(id);
    };
  }, [n]);
  return (
    <section className="grid gap-2" aria-label="Open games" data-testid="open-games">
      <div className="flex items-center justify-between gap-2">
        <h2 className="font-display font-black text-2xl flex items-center gap-2"><Globe size={22} aria-hidden /> Open games</h2>
        <button className="btn btn-sm btn-ghost" onClick={() => setN((x) => x + 1)} aria-label="Refresh open games"><RefreshCw size={16} /></button>
      </div>
      {error && !games ? <p className="text-mute text-sm">Couldn’t load open games. Check your connection.</p> : !games ? <p className="text-mute text-sm">Looking for games…</p> : games.length ? (
        <ul className="grid gap-2">
          {games.map((g) => {
            const icon = ART[g.gameId]?.icon;
            return (
              <li key={g.code} className="card p-3 flex items-center gap-3" data-testid="open-game">
                {icon ? <img src={icon} alt="" aria-hidden width={44} height={44} className="size-11 object-contain shrink-0" /> : <Globe size={36} className="shrink-0 text-mute" aria-hidden />}
                <div className="flex-1 min-w-0">
                  <p className="font-bold truncate">{g.game}</p>
                  <p className="text-xs text-mute flex items-center gap-1 truncate">
                    <Avatar id={g.hostAvatar} size={16} /> {g.host}’s party · {g.players + g.bots}/{g.capacity}{g.bots ? ` (${g.bots} AI)` : ''}{g.approval ? ' · host lets you in' : ''}
                  </p>
                </div>
                <button
                  className="btn btn-sm"
                  disabled={inParty}
                  title={inParty ? 'Leave your party first' : undefined}
                  onClick={() => { sound.play('tap'); navigate({ name: 'join', code: g.code }); }}
                  data-testid={`open-game-join-${g.code}`}
                >
                  Join
                </button>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="text-mute text-sm">No public games right now. Hosts can list theirs with Party settings → Public party.</p>
      )}
      {inParty && games && games.length > 0 && <p className="text-xs text-mute">Leave your party to join another one.</p>}
    </section>
  );
}
